import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { BRAND, PHONE_DISPLAY, PHONE_TEL } from '../constants/brand';
import { NAV_SERVICES } from '../data/services';
import { ROUTES, servicePath } from '../enum/routes';

const IconOpen = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const IconClose = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

function navClass({ isActive }: { isActive: boolean }) {
  return `text-sm font-medium transition-colors hover:text-forest ${isActive ? 'text-forest' : 'text-ink/80'}`;
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const close = () => setOpen(false);

  return (
    <header className="site-header sticky top-0 z-40 border-b border-black/5 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/75">
      <div className="container-page site-header__bar flex items-center justify-between">
        <Link className="site-logo" to={ROUTES.HOME} onClick={close}>
          <img src="/assets/img/logo.png" alt={BRAND} width={132} height={116} />
        </Link>

        <nav className="hidden items-center gap-8 md:flex">
          <div className="group relative">
            <button
              type="button"
              aria-haspopup="true"
              className="inline-flex items-center gap-1 text-sm font-medium text-ink/80 transition-colors hover:text-forest group-hover:text-forest group-focus-within:text-forest"
            >
              Services
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <div className="invisible absolute left-1/2 top-full z-50 -translate-x-1/2 pt-3 opacity-0 transition duration-150 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
              <ul className="w-72 rounded-xl border border-black/10 bg-white p-2 shadow-lg">
                {NAV_SERVICES.map((s) => (
                  <li key={s.slug}>
                    <Link className="block rounded-lg px-3 py-2 text-sm font-medium text-ink/80 hover:bg-forest-tint hover:text-forest" to={servicePath(s.slug)}>
                      {s.name}
                    </Link>
                  </li>
                ))}
                <li className="mt-1 border-t border-black/5 pt-1">
                  <Link className="block rounded-lg px-3 py-2 text-sm font-semibold text-forest hover:bg-forest-tint" to={ROUTES.SERVICES}>
                    All services →
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          <NavLink className={navClass} to={ROUTES.SERVICE_AREAS}>
            Service Areas
          </NavLink>
          <NavLink className={navClass} to={ROUTES.QUOTE}>
            Contact
          </NavLink>
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <a href={`tel:${PHONE_TEL}`} className="text-sm font-semibold text-forest hover:text-forest-hover">
            {PHONE_DISPLAY}
          </a>
          <Link className="btn-primary !py-2 !text-sm" to={ROUTES.QUOTE}>
            Get a Free Quote
          </Link>
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <a href={`tel:${PHONE_TEL}`} aria-label="Call us" className="rounded-md p-2 text-forest hover:bg-forest-tint">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
            </svg>
          </a>
          <button
            type="button"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            className="rounded-md p-2 text-ink hover:bg-forest-tint"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <IconClose /> : <IconOpen />}
          </button>
        </div>
      </div>

      <div className={`site-menu md:hidden ${open ? 'is-open' : ''}`} hidden={!open}>
        <div className="site-menu__panel border-t border-black/5 bg-white px-4 py-6">
          <nav className="flex flex-col gap-1">
            <div className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-ink/45">Services</div>
            {NAV_SERVICES.map((s) => (
              <Link key={s.slug} className="rounded-md px-3 py-3 text-base font-medium text-ink hover:bg-forest-tint" to={servicePath(s.slug)} onClick={close}>
                {s.name}
              </Link>
            ))}
            <Link className="rounded-md px-3 py-3 text-base font-medium text-ink hover:bg-forest-tint" to={ROUTES.SERVICES} onClick={close}>
              All Services
            </Link>
            <Link className="rounded-md px-3 py-3 text-base font-medium text-ink hover:bg-forest-tint" to={ROUTES.SERVICE_AREAS} onClick={close}>
              Service Areas
            </Link>
            <Link className="rounded-md px-3 py-3 text-base font-medium text-ink hover:bg-forest-tint" to={ROUTES.QUOTE} onClick={close}>
              Contact
            </Link>
          </nav>
          <div className="mt-4 flex flex-col gap-2">
            <a href={`tel:${PHONE_TEL}`} className="btn-secondary w-full">
              Call {PHONE_DISPLAY}
            </a>
            <Link className="btn-primary w-full" to={ROUTES.QUOTE} onClick={close}>
              Get a Free Quote
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
